import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { ButtonKind } from '@components/Button/Button';
import { Button } from '@components/Button/Button';
import * as tokens from '@tokens';
import { ZINDEX } from '@utils/constants';
import { CloseAction } from '@components/Actions/CloseAction/CloseAction';
import type { IconName } from '@components/Icon/BaseIcon';
import type { SyntheticEvent } from 'react';
import { useStrings } from '@hooks/useStrings';
import { useInterpolate } from '@hooks/useInterpolate';

export type ActionConfigProps = {
  /**
   * Kind of button used.
   */
  kind?: ButtonKind;
  /**
   * Text content of the button.
   */
  text: string;
  /**
   * Optional icon for the button.
   */
  icon?: IconName;
  /**
   * Callback for the click action.
   */
  onClick: (e: SyntheticEvent<HTMLButtonElement>) => void;
  /**
   * Option to close the modal or let it open when a button is clicked.
   */
  isModalClosingOnClick?: boolean;
  /**
   * Option to hide the button when a certain condition happens.
   */
  isHidden?: boolean;
};

export interface SelectedRowsActionProps {
  /**
   * Number of rows that are selected in the table. This is used for the banner default message.
   * Defaults to 0.
   */
  numberRowsSelected?: number;

  /**
   * Message to display in the action banner.
   */
  actionMessage?: string;

  /**
   * Configuration for the first action button (on the left).
   */
  leftActionProps: ActionConfigProps;

  /**
   * Configuration for the second action button (on the right).
   */
  rightActionProps?: ActionConfigProps;

  /**
   * Action to be executed when the close button is clicked on.
   */
  onClose: () => void;
}

const StyledBanner = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  background-color: ${tokens.color.ui_background};
  ${tokens.elevation(3)};
  border-radius: ${tokens.radius.m};
  padding: ${tokens.spacer.x2} ${tokens.spacer.x3};
  bottom: 10%;
  ${tokens.text('subtitle1')};
  z-index: ${ZINDEX.TOAST};
`;

/**
 * SelectedRowsAction component.
 *
 * @summary Provides a banner with actions for selected rows in a table.
 * @private
 */
export const SelectedRowsAction = ({
  actionMessage,
  numberRowsSelected = 0,
  leftActionProps,
  rightActionProps,
  onClose,
}: SelectedRowsActionProps): JSX.Element => {
  const { actions, table } = useStrings();
  const interpolate = useInterpolate();

  const defaultMessage =
    numberRowsSelected === 1
      ? table.oneSelectedItem
      : interpolate(table.multipleSelectedItems, { numberRowsSelected });
  return (
    <StyledBanner data-testid="ds-table-selectable-action-banner">
      {actionMessage || defaultMessage}
      {!leftActionProps?.isHidden && (
        <Button
          kind={leftActionProps.kind}
          icon={leftActionProps.icon}
          size="s"
          css={css`
            margin-left: ${tokens.spacer.x3};
            margin-right: ${tokens.spacer.x1};
          `}
          onClick={leftActionProps.onClick}
        >
          {leftActionProps.text}
        </Button>
      )}
      {rightActionProps && !rightActionProps?.isHidden && (
        <Button
          kind={rightActionProps.kind}
          icon={rightActionProps.icon}
          size="s"
          css={css`
            margin-left: ${leftActionProps.isHidden
              ? tokens.spacer.x3
              : tokens.spacer.x0};
            margin-right: ${tokens.spacer.x1};
          `}
          onClick={rightActionProps.onClick}
        >
          {rightActionProps.text}
        </Button>
      )}
      <CloseAction
        description={actions.close}
        onClick={onClose}
        css={css`
          margin-left: ${tokens.spacer.x0};
        `}
      />
    </StyledBanner>
  );
};
